import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  CalendarDays, Library, BookOpen, Receipt, Settings, MessageCircle, Sparkles,
  ShieldCheck, FileText, Lock, Users, Users2, BarChart3, Heart, Bell, LogOut, GraduationCap, Tag, ListTodo, Search, School, MailWarning,
} from 'lucide-react';
import { THERAPIST_MENU_GROUPS, PARENT_MENU_GROUPS } from './navConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const ICON_MAP = {
  CalendarDays, Library, BookOpen, Receipt, Settings, MessageCircle, Sparkles,
  ShieldCheck, FileText, Lock, Users, Users2, BarChart3, Heart, Bell, GraduationCap, Tag, ListTodo, Search, School, MailWarning,
};

export default function MenuDrawer({ open, onOpenChange, user }) {
  const navigate = useNavigate();
  // Keep a fresh copy of the user in local state — when the drawer opens
  // we re-fetch /api/auth/me so newly-granted flags (e.g. has_parent_app_access
  // set by admin while the therapist was mid-session) show up without them
  // having to log out and back in.
  const [freshUser, setFreshUser] = useState(user);

  useEffect(() => { setFreshUser(user); }, [user]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const refresh = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        setFreshUser(res.data);
        // Sync localStorage so the rest of the app also picks up the flag.
        try { localStorage.setItem('user', JSON.stringify(res.data)); } catch {}
      } catch { /* silent */ }
    };
    refresh();
    return () => { cancelled = true; };
  }, [open]);

  const isSuperAdmin = !!freshUser?.is_super_admin;
  const hasParentAppAccess = !!freshUser?.has_parent_app_access;
  const groups =
    freshUser?.role === 'parent' ? PARENT_MENU_GROUPS() : THERAPIST_MENU_GROUPS(isSuperAdmin, hasParentAppAccess);

  const handleNav = (item) => {
    if (item.external && item.path?.startsWith('/')) {
      window.open(item.path, '_blank', 'noopener,noreferrer');
    } else {
      navigate(item.path);
    }
    onOpenChange(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 bg-[#FDFBF7] border-l border-stone-200 overflow-y-auto"
        data-testid="nav-menu-drawer"
      >
        <SheetHeader className="px-6 pt-6 pb-3 border-b border-stone-100">
          <SheetTitle className="font-serif text-2xl text-stone-900 text-left">
            Menu
          </SheetTitle>
          {user?.name && (
            <p className="text-sm text-stone-500 text-left">
              Signed in as {user.name}
              {user.role === 'parent' && ' (parent)'}
              {isSuperAdmin && ' (admin)'}
            </p>
          )}
        </SheetHeader>

        <div className="px-4 py-2 space-y-6 pb-8">
          {groups.map((group) => (
            <div key={group.label}>
              <p className="px-2 mb-1 text-[11px] uppercase tracking-wider font-semibold text-stone-400">
                {group.label}
              </p>
              <ul className="space-y-0.5">
                {group.items.map((item) => {
                  const Icon = ICON_MAP[item.icon] || BookOpen;
                  return (
                    <li key={item.path + item.label}>
                      <button
                        onClick={() => handleNav(item)}
                        className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white transition-colors text-left"
                        data-testid={`menu-link-${item.path.replace(/[^a-z0-9]/gi, '-')}`}
                      >
                        <Icon className="w-5 h-5 text-stone-500 shrink-0" />
                        <span className="text-stone-800 text-[15px]">{item.label}</span>
                        {item.external && (
                          <span className="ml-auto text-[10px] uppercase tracking-wider text-stone-400">
                            new tab
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

          {/* Sign out */}
          <div className="pt-4 border-t border-stone-200">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-red-50 transition-colors text-left"
              data-testid="menu-logout-btn"
            >
              <LogOut className="w-5 h-5 text-red-500 shrink-0" />
              <span className="text-red-700 text-[15px] font-medium">Sign out</span>
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
